'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- Bundle upsell step follows fixed Figma layout values. */

import { BRAND_NAMES } from '@hyphengroup/cms-components/modules/segment';
import { CmsOffer } from '@hyphengroup/cms-components/types/cmsProduct';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { BundleCardGroup } from '$/(cms)/-cms/apply-v3/[[...slugs]]/components/BundleCardGroup';
import { ApplyStaticText } from '$/(cms)/-cms/apply-v3/[[...slugs]]/i18n';
import { BundleUpsellContext } from '$/(cms)/-cms/apply-v3/[[...slugs]]/lib/bundleTracking';
import {
  getActiveBundleOffers,
  getBundlePreviewOffer,
  getBundleRewardName,
  getHighestBundleRewardDisplay,
  getStandaloneRewardName,
} from '$/(cms)/-cms/apply-v3/[[...slugs]]/lib/helpers';
import { CmsApplyRewardProduct } from '$/(cms)/_modules/cmsService/types/applyRewardProduct';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

type Props = {
  product: CmsApplyRewardProduct;
  selectedOffer: CmsOffer | undefined;
  trackingContext?: BundleUpsellContext;
  onSelectionChange: (offer: CmsOffer | undefined, index?: number) => void;
};

export function BundleUpsellContent({
  product,
  selectedOffer,
  trackingContext,
  onSelectionChange,
}: Props) {
  const context = useClientContext();
  const staticText = getClientTranslations(ApplyStaticText, context);
  const offers = getActiveBundleOffers(product);
  const previewOffer = getBundlePreviewOffer(product, selectedOffer);
  const currentRewardLabel = getStandaloneRewardName(product);
  const upgradeRewardLabel = previewOffer
    ? getBundleRewardName(previewOffer)
    : undefined;

  const productName = product.catalogueProduct?.product_name ?? '';
  const rewardValue = getHighestBundleRewardDisplay(product);
  const heroDescription = rewardValue
    ? tr('bundleHeroDescription', staticText, { productName, rewardValue })
    : tr('bundleHeroDescriptionNoValue', staticText, { productName });

  return (
    <div className="flex w-full flex-col gap-spacing/gap/y md:gap-spacing/gap/section-y">
      <div className="flex flex-col gap-spacing/gap/y-sm">
        <div className="flex flex-col gap-2">
          <h2 className="mhg-alpha-title-1 md:mhg-alpha-display-1 text-text/primary">
            {staticText.bundleHeroTitle}
          </h2>
          <p className="mhg-alpha-typography-body-2-relaxed md:mhg-alpha-typography-body-1-relaxed text-text/secondary-lighter">
            {heroDescription}
          </p>
        </div>

        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-radius/card-inner px-4 py-3 text-center bg-[#dff4ff] md:px-6 md:py-4">
          <BundleAnchorColumn
            label={staticText.yourCurrent}
            value={currentRewardLabel}
          />
          <MaterialIcon
            className="text-text/secondary-lighter"
            icon="chevron_right"
            px={20}
          />
          <BundleAnchorColumn
            label={staticText.upgradeTo}
            value={upgradeRewardLabel}
          />
        </div>
      </div>

      <BundleCardGroup
        offers={offers}
        product={product}
        trackingContext={trackingContext}
        onSelectionChange={onSelectionChange}
      />

      <p className="mhg-alpha-typography-caption text-text/secondary-lighter">
        {staticText.bundleDisclaimer}
      </p>
    </div>
  );
}

function BundleAnchorColumn(props: {
  label: string;
  value: string | undefined;
}) {
  const context = useClientContext();
  const staticText = getClientTranslations(ApplyStaticText, context);

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="mhg-alpha-typography-caption text-text/secondary-lighter">
        {props.label}
      </span>
      <span className="mhg-alpha-typography-body-2 md:mhg-alpha-typography-body-1 break-words text-text/primary">
        {props.value ??
          tr('noSelectedExclusiveOffer', staticText, {
            brand: BRAND_NAMES[context.brand],
          })}
      </span>
    </div>
  );
}
